import { zodResolver } from '@hookform/resolvers/zod';
import { type LoginInput, loginSchema } from '@sea-snag/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@sea-snag/ui';
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useAuth, useUser } from '../hooks/use-auth';
import { invitationsApi } from '../lib/api-client';
import { returnPath } from '../lib/return-path';
import { pickInitialOrg } from '../stores/org-store';

const loginSearchSchema = z.object({
  inviteToken: z.string().optional(),
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: loginSearchSchema,
});

function LoginPage() {
  const navigate = useNavigate();
  const { login, refetchUser } = useAuth();
  const { isAuthenticated, user } = useUser();
  const { inviteToken, redirect } = Route.useSearch();
  // The page the visitor was sent here from, to open once they're signed in.
  const back = returnPath(redirect, window.location.origin);
  const [error, setError] = useState<string | null>(null);
  // Guards the auto-link side effect — without this, StrictMode's double
  // render would fire the link POST twice for a single token.
  const linkAttemptedFor = useRef<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  async function applyInvitationAndNavigate(token: string) {
    try {
      const result = await invitationsApi.linkExistingUser(token);
      await refetchUser();
      toast.success(`Joined as ${result.role}`);
      await navigate({
        to: '/$orgPid/dashboard',
        params: { orgPid: result.organizationPid },
      });
      return true;
    } catch (err) {
      if (err instanceof HTTPError) {
        const body = (await err.response.json().catch(() => ({}))) as { message?: string };
        toast.error(body.message ?? 'Could not attach invitation');
      } else {
        toast.error('Could not attach invitation');
      }
      return false;
    }
  }

  // Existing user + invite token → attach the invite once (ref-guarded so
  // StrictMode's double mount doesn't double-POST), then on failure fall back
  // to the default dashboard redirect.
  useEffect(() => {
    if (!isAuthenticated || !user || !inviteToken) return;
    if (linkAttemptedFor.current === inviteToken) return;
    linkAttemptedFor.current = inviteToken;

    void applyInvitationAndNavigate(inviteToken).then((ok) => {
      if (ok) return;
      if (back) {
        void navigate({ href: back });
        return;
      }
      if (user.isSuperAdmin) {
        void navigate({ to: '/admin/dashboard' });
        return;
      }
      const orgs = (user.organizations ?? []).map((o) => ({
        pid: o.id,
        name: o.name,
        slug: o.slug,
        setupCompletedAt: o.setupCompletedAt,
        role: o.role,
      }));
      const org = pickInitialOrg(orgs);
      if (org) {
        void navigate({ to: '/$orgPid/dashboard', params: { orgPid: org.pid } });
      }
    });
  }, [isAuthenticated, user, inviteToken, back]);

  if (isAuthenticated && user) {
    if (inviteToken) {
      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground">Linking your invitation…</p>
            </CardContent>
          </Card>
        </div>
      );
    }
    if (back) {
      // The router goes to `href` and ignores `to` when both are set.
      return <Navigate to="." href={back} replace />;
    }
    if (user.isSuperAdmin) {
      return <Navigate to="/admin/dashboard" />;
    }
    const orgs = (user.organizations ?? []).map((o) => ({
      pid: o.id,
      name: o.name,
      slug: o.slug,
      setupCompletedAt: o.setupCompletedAt,
      role: o.role,
    }));
    const org = pickInitialOrg(orgs);
    if (org) {
      return <Navigate to="/$orgPid/dashboard" params={{ orgPid: org.pid }} />;
    }
  }

  const onSubmit = async (data: LoginInput) => {
    setError(null);
    try {
      const loggedInUser = await login(data);
      if (inviteToken) {
        const ok = await applyInvitationAndNavigate(inviteToken);
        if (ok) return;
      }
      if (back) {
        await navigate({ href: back });
        return;
      }
      if (loggedInUser.isSuperAdmin) {
        await navigate({ to: '/admin/dashboard' });
      } else {
        const orgs = (loggedInUser.organizations ?? []).map((o) => ({
          pid: o.id,
          name: o.name,
          slug: o.slug,
          setupCompletedAt: o.setupCompletedAt,
          role: o.role,
        }));
        const org = pickInitialOrg(orgs);
        await navigate({ to: '/$orgPid/dashboard', params: { orgPid: org?.pid ?? '' } });
      }
    } catch (err) {
      if (err instanceof HTTPError) {
        const body = (await err.response.json().catch(() => ({}))) as { message?: string };
        setError(body.message || 'Invalid email or password');
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Sign in</CardTitle>
          <CardDescription>
            {inviteToken
              ? 'Sign in to accept the invitation that was sent to your email.'
              : 'Enter your email and password to access your account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" aria-label="Sign in form">
            {error && (
              <div
                role="alert"
                className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
              >
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                aria-describedby={errors.email ? 'login-email-error' : undefined}
                {...register('email')}
              />
              {errors.email && (
                <p id="login-email-error" className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary underline-offset-4 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                aria-describedby={errors.password ? 'login-password-error' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <p id="login-password-error" className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
